import { useEffect, useState } from 'react';
import axios from 'axios';
import { FaPaperPlane, FaEdit, FaTrash, FaRedo, FaBullhorn } from 'react-icons/fa';
import { API_BASE } from '../utils/helpers';
import UiModal from '../components/UiModal';

export default function Promotions() {
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [promotions, setPromotions] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [editShow, setEditShow] = useState(false);
  const [editData, setEditData] = useState({ id: null, title: '', body: '', image_url: '' });
  const [editImage, setEditImage] = useState(null);
  const [editPreview, setEditPreview] = useState(null);
  const [resendLoadingId, setResendLoadingId] = useState(null);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
    setPreview(file ? URL.createObjectURL(file) : null);
  };

  const fetchPromotions = async () => {
    try {
      setListLoading(true);
      const { data } = await axios.get(`${API_BASE}/api/promotion/all?perPage=100`);
      setPromotions(data || []);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to load promotions');
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => { fetchPromotions(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !body) { setError('All fields are required'); return; }
    setLoading(true); setSuccess(''); setError('');
    const formData = new FormData();
    formData.append('file', image);
    formData.append('title', title);
    formData.append('body', body);
    try {
      const { data } = await axios.post(`${API_BASE}/api/promotion/send-promotion`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(data.message || 'Promotion sent successfully!');
      setImage(null); setPreview(null); setTitle(''); setBody('');
      fetchPromotions();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send promotion');
    } finally {
      setLoading(false);
    }
  };

  const confirmAndDelete = async (id) => {
    if (!window.confirm('Delete this promotion?')) return;
    try {
      const { data } = await axios.delete(`${API_BASE}/api/promotion/${id}`);
      setSuccess(data.message || 'Promotion deleted');
      await fetchPromotions();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  const resendPromotion = async (id) => {
    try {
      setError(''); setSuccess(''); setResendLoadingId(id);
      try {
        const { data } = await axios.post(`${API_BASE}/api/promotion/${id}/resend`);
        setSuccess(data.message || 'Promotion resent');
        return;
      } catch (apiErr) {
        if (apiErr.response?.status !== 404) throw apiErr;
      }
      const promo = promotions.find((p) => p.id === id);
      if (!promo) throw new Error('Promotion not found locally');
      const fd = new FormData();
      fd.append('title', promo.title || '');
      fd.append('body', promo.body || '');
      if (promo.image_url) fd.append('image_url', promo.image_url);
      if (promo.image_url) {
        try {
          const imgRes = await axios.get(promo.image_url, { responseType: 'blob' });
          const blob = imgRes.data;
          const file = new File([blob], `resend_${id}.jpg`, { type: blob.type || 'image/jpeg' });
          fd.append('file', file);
        } catch (imgErr) { /* backend uses default image */ }
      }
      const { data } = await axios.post(`${API_BASE}/api/promotion/send-promotion`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(data.message || 'Promotion resent');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to resend promotion');
    } finally {
      setResendLoadingId(null);
    }
  };

  const openEdit = (p) => {
    setEditData({ id: p.id, title: p.title || '', body: p.body || '', image_url: p.image_url || '' });
    setEditImage(null); setEditPreview(null); setEditShow(true);
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    setEditImage(file || null);
    setEditPreview(file ? URL.createObjectURL(file) : null);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editData.id) return;
    try {
      const fd = new FormData();
      if (editImage) fd.append('file', editImage);
      fd.append('title', editData.title);
      fd.append('body', editData.body);
      const { data } = await axios.put(`${API_BASE}/api/promotion/${editData.id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(data.message || 'Promotion updated');
      setEditShow(false);
      await fetchPromotions();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update');
    }
  };

  const fileInput =
    'block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-indigo-700 hover:file:bg-indigo-100';

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900">Promotions</h2>
        <p className="text-sm text-slate-500">Compose and send push notifications to the app.</p>
      </div>

      {success && <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-700">{success}</div>}
      {error && <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-700">{error}</div>}

      {/* Composer */}
      <form onSubmit={handleSubmit} className="ui-card mb-6 p-6">
        <h3 className="mb-4 text-sm font-semibold text-slate-900">Send push notification</h3>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Promotion image</label>
            <input type="file" accept="image/*" onChange={handleImageChange} className={fileInput} />
            {preview && <img src={preview} alt="preview" className="mt-3 h-28 w-28 rounded-lg border border-slate-200 object-cover" />}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Title</label>
            <input className="ui-input" type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Enter promotion title" required />
          </div>
          <div className="lg:col-span-2">
            <label className="mb-1 block text-sm font-medium text-slate-700">Description</label>
            <textarea className="ui-input" rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Enter notification message" required />
          </div>
        </div>
        <div className="mt-4">
          <button type="submit" className="ui-btn-primary" disabled={loading}>
            <FaPaperPlane className="text-xs" /> {loading ? 'Sending…' : 'Send promotion'}
          </button>
        </div>
      </form>

      {/* Existing */}
      <div className="ui-card overflow-hidden">
        <div className="border-b border-slate-200 px-5 py-3">
          <h3 className="text-sm font-semibold text-slate-900">Existing promotions</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="ui-table">
            <thead>
              <tr>
                <th className="w-12">#</th>
                <th>Image</th>
                <th>Title</th>
                <th>Description</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {listLoading &&
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <td key={j}><div className="h-4 w-full max-w-[120px] animate-pulse rounded bg-slate-100" /></td>
                    ))}
                  </tr>
                ))}

              {!listLoading && promotions.length === 0 && (
                <tr>
                  <td colSpan={5}>
                    <div className="flex flex-col items-center justify-center gap-2 py-12 text-slate-400">
                      <FaBullhorn className="text-2xl" />
                      <p className="text-sm">No promotions yet.</p>
                    </div>
                  </td>
                </tr>
              )}

              {!listLoading && promotions.map((p, idx) => (
                <tr key={p.id}>
                  <td className="text-slate-400">{idx + 1}</td>
                  <td>
                    {p.image_url ? (
                      <img src={p.image_url} alt="promo" className="h-11 w-11 rounded-lg border border-slate-200 object-cover" />
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="font-medium text-slate-900">{p.title}</td>
                  <td className="max-w-[360px] truncate text-slate-500" title={p.body}>{p.body}</td>
                  <td>
                    <div className="flex items-center justify-end gap-2">
                      <button className="ui-btn-ghost px-2.5 py-1.5 text-xs" onClick={() => openEdit(p)}>
                        <FaEdit className="text-xs" /> Edit
                      </button>
                      <button
                        className="ui-btn-ghost px-2.5 py-1.5 text-xs text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                        disabled={resendLoadingId === p.id}
                        onClick={() => resendPromotion(p.id)}
                      >
                        <FaRedo className="text-xs" /> {resendLoadingId === p.id ? 'Resending…' : 'Resend'}
                      </button>
                      <button
                        className="ui-btn-ghost px-2.5 py-1.5 text-xs text-rose-600 hover:bg-rose-50"
                        onClick={() => confirmAndDelete(p.id)}
                      >
                        <FaTrash className="text-xs" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit modal */}
      <UiModal
        open={editShow}
        title="Edit Promotion"
        onClose={() => setEditShow(false)}
        footer={
          <>
            <button className="ui-btn-ghost" onClick={() => setEditShow(false)}>Cancel</button>
            <button className="ui-btn-primary" onClick={saveEdit}>Save</button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Image</label>
            {(editPreview || editData.image_url) && (
              <img src={editPreview || editData.image_url} alt="promo" className="mb-2 h-28 w-28 rounded-lg border border-slate-200 object-cover" />
            )}
            <input type="file" accept="image/*" onChange={handleEditImageChange} className={fileInput} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Title</label>
            <input className="ui-input" type="text" value={editData.title} onChange={(e) => setEditData({ ...editData, title: e.target.value })} required />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Description</label>
            <textarea className="ui-input" rows={3} value={editData.body} onChange={(e) => setEditData({ ...editData, body: e.target.value })} required />
          </div>
        </div>
      </UiModal>
    </div>
  );
}
